// corner.js — Stage 2 of the procedural-ruin pipeline.
//
// Given the set of detected XRPlanes, find a "corner candidate" —
// two roughly-perpendicular vertical planes whose polygons share a
// near-coincident vertex pair, sitting on (or close to) a horizontal
// plane. Render a glowing marker at the corner so the user can see
// where Stage 3 will build the ruin.
//
// Stage 3 will replace the marker with the actual procedural wall mesh.

import * as THREE from 'three'
import * as planes from './planes.js'

// Walls must be roughly perpendicular. 90° ± 60° accepted (i.e. angle
// between normals in [30°, 150°]). Generous because ARCore can fit a
// wall plane at a noticeable angle when the polygon only covers part
// of the wall surface — the user's first 2-walls-detected test gave us
// 40° between wall normals on a visually-perfect 90° corner.
const PERPENDICULAR_TOLERANCE_RAD = (60 * Math.PI) / 180

// Reject candidate corners where the corner point is more than this
// far from either wall's centroid. ARCore wall polygons can drift,
// but a real corner is usually within a couple of meters of both
// wall midpoints.
const MAX_CORNER_FROM_WALL_M = 4.0

// How often to log diagnostic info when we have walls but no corner.
const DIAGNOSTIC_INTERVAL_MS = 3000

// ARCore drops walls aggressively (a few seconds without seeing them =
// gone). To find a corner from two walls that aren't simultaneously
// detected, remember each wall's last-known pose for this long.
const WALL_MEMORY_MS = 5000

// Two remembered walls are "the same wall" if their normals are within
// ~10° AND their centroids are within this much. Used to dedupe
// across sightings.
const SAME_WALL_NORMAL_DOT_MIN = 0.985 // ~10°
const SAME_WALL_CENTROID_DIST_M = 1.0

// Visual marker dimensions.
const MARKER_HEIGHT_M = 1.4
const MARKER_RADIUS_M = 0.04
const MARKER_COLOR = 0xffaa00

let _scene = null
let _cornerGroup = null
let _markerMesh = null
let _currentCandidate = null
let _lastDiagnosticAt = 0
let _hadCornerLastFrame = false
// Remembered walls: array of { normal, centroid, worldPoints, seenAt }.
// Survives ARCore dropping a wall; expired by WALL_MEMORY_MS.
let _wallMemory = []
let _floorMemory = []

export function initCorner(scene) {
  _scene = scene
  _cornerGroup = new THREE.Group()
  _cornerGroup.name = 'corner'
  scene.add(_cornerGroup)
  _markerMesh = null
  _currentCandidate = null
  _lastDiagnosticAt = 0
  _hadCornerLastFrame = false
  _wallMemory = []
  _floorMemory = []
}

// Returns the corner candidate (or null). Side effect: shows/hides the
// marker mesh.
export function updateCorner(frame, localSpace) {
  if (!_cornerGroup || !frame || !localSpace) {
    hideMarker()
    _currentCandidate = null
    return null
  }

  const detected = planes.getDetectedPlanes()
  if (!detected || detected.length < 2) {
    hideMarker()
    _currentCandidate = null
    return null
  }

  // Compute world-space polygons + normals once per plane this frame.
  // Don't trust plane.orientation — Chrome/ARCore on some devices gets it
  // backwards. Classify by the actual world-space normal direction.
  const now = performance.now()
  for (const plane of detected) {
    const w = poseToWorld(plane, frame, localSpace)
    if (!w) continue
    const absNy = Math.abs(w.normal.y)
    if (absNy >= 0.7) rememberPlane(_floorMemory, w, now)
    else if (absNy <= 0.3) rememberPlane(_wallMemory, w, now)
    // else: ambiguous tilt, ignore for corner detection
  }

  // Drop expired memories.
  _wallMemory = _wallMemory.filter((w) => now - w.seenAt <= WALL_MEMORY_MS)
  _floorMemory = _floorMemory.filter((f) => now - f.seenAt <= WALL_MEMORY_MS)

  const walls = _wallMemory
  const floors = _floorMemory

  if (walls.length < 2 || floors.length === 0) {
    hideMarker()
    _currentCandidate = null
    if (_hadCornerLastFrame) console.info('[corner] lost (insufficient planes)')
    _hadCornerLastFrame = false
    return null
  }

  const best = findBestCorner(walls, floors)
  if (!best) {
    hideMarker()
    _currentCandidate = null
    maybeLogDiagnostic(walls, floors)
    if (_hadCornerLastFrame) console.info('[corner] lost (no qualifying pair)')
    _hadCornerLastFrame = false
    return null
  }

  showMarker(best.corner)
  _currentCandidate = best
  if (!_hadCornerLastFrame) {
    console.info(
      `[corner] FOUND at (${best.corner.x.toFixed(2)}, ${best.corner.y.toFixed(2)}, ${best.corner.z.toFixed(2)})`
    )
  }
  _hadCornerLastFrame = true
  return best
}

export function getCornerCandidate() {
  return _currentCandidate
}

export function hasCorner() {
  return _currentCandidate !== null
}

// Show/hide the corner marker group. Used to declutter once the ruin
// is built — the user no longer cares about the auto-detected corner.
export function setVisible(visible) {
  if (_cornerGroup) _cornerGroup.visible = visible
}

export function clearCorner() {
  if (_markerMesh) {
    _cornerGroup.remove(_markerMesh)
    _markerMesh.geometry.dispose()
    _markerMesh.material.dispose()
    _markerMesh = null
  }
  _currentCandidate = null
  _wallMemory = []
  _floorMemory = []
}

// Adds a plane sighting to the memory. If we've seen the same surface
// recently (similar normal + nearby centroid), update its entry in
// place; otherwise append.
function rememberPlane(memory, sighting, now) {
  for (const m of memory) {
    const dot = m.normal.dot(sighting.normal)
    if (dot < SAME_WALL_NORMAL_DOT_MIN) continue
    if (m.centroid.distanceTo(sighting.centroid) > SAME_WALL_CENTROID_DIST_M) continue
    // Same plane — refresh in place.
    m.normal = sighting.normal
    m.centroid = sighting.centroid
    m.worldPoints = sighting.worldPoints
    m.seenAt = now
    return
  }
  memory.push({
    normal: sighting.normal,
    centroid: sighting.centroid,
    worldPoints: sighting.worldPoints,
    seenAt: now
  })
}

// ---------------------------------------------------------------------------

// For each pair of walls, geometrically intersect the wall planes (gives
// a vertical line), then intersect that line with the nearest floor plane
// (gives the corner point on the floor). Doesn't require the wall
// polygons to actually reach the corner — uses only the plane equations.
function findBestCorner(walls, floors) {
  let best = null

  for (let i = 0; i < walls.length; i++) {
    for (let j = i + 1; j < walls.length; j++) {
      const a = walls[i]
      const b = walls[j]

      // Reject roughly-parallel walls. They don't form a corner.
      const dot = Math.abs(a.normal.dot(b.normal))
      const angleFromPerp = Math.abs(Math.acos(Math.min(1, dot)) - Math.PI / 2)
      if (angleFromPerp > PERPENDICULAR_TOLERANCE_RAD) continue

      const planeA = new THREE.Plane().setFromNormalAndCoplanarPoint(a.normal, a.centroid)
      const planeB = new THREE.Plane().setFromNormalAndCoplanarPoint(b.normal, b.centroid)

      const line = planePlaneIntersection(planeA, planeB)
      if (!line) continue

      // Nearest floor plane to either wall's centroid (proxy for "the
      // floor under this corner").
      const floor = nearestFloor(a.centroid, floors)
      const floorPlane = new THREE.Plane().setFromNormalAndCoplanarPoint(
        floor.normal,
        floor.centroid
      )

      const corner = lineFloorIntersection(line.point, line.direction, floorPlane)
      if (!corner) continue

      const distA = corner.distanceTo(a.centroid)
      const distB = corner.distanceTo(b.centroid)
      if (distA > MAX_CORNER_FROM_WALL_M || distB > MAX_CORNER_FROM_WALL_M) continue

      const score = distA + distB
      if (!best || score < best.score) {
        best = { corner, wallA: a, wallB: b, floor, score }
      }
    }
  }
  return best
}

// Intersection line of two planes. Returns { point, direction } or null
// if the planes are parallel.
function planePlaneIntersection(p1, p2) {
  const direction = new THREE.Vector3().crossVectors(p1.normal, p2.normal)
  const lenSq = direction.lengthSq()
  if (lenSq < 1e-6) return null
  direction.normalize()

  // THREE.Plane uses `normal · x + constant = 0`, so the offset is
  // `d = -constant`. The standard formula for a point on the line:
  //   P = (d1 * (n2 × dir) + d2 * (dir × n1)) / |dir|²
  const d1 = -p1.constant
  const d2 = -p2.constant

  const n2_cross_dir = new THREE.Vector3().crossVectors(p2.normal, direction)
  const dir_cross_n1 = new THREE.Vector3().crossVectors(direction, p1.normal)

  const point = new THREE.Vector3()
    .copy(n2_cross_dir)
    .multiplyScalar(d1)
    .addScaledVector(dir_cross_n1, d2) // direction is already normalized → dividing by 1
  return { point, direction }
}

function lineFloorIntersection(linePoint, lineDir, floorPlane) {
  const denom = floorPlane.normal.dot(lineDir)
  if (Math.abs(denom) < 1e-6) return null // line is parallel to the floor
  const numer = -floorPlane.constant - floorPlane.normal.dot(linePoint)
  const t = numer / denom
  return new THREE.Vector3().copy(linePoint).addScaledVector(lineDir, t)
}

function nearestFloor(point, floors) {
  let best = floors[0]
  let bestDist = best.centroid.distanceTo(point)
  for (let i = 1; i < floors.length; i++) {
    const d = floors[i].centroid.distanceTo(point)
    if (d < bestDist) {
      bestDist = d
      best = floors[i]
    }
  }
  return best
}

// When we have walls but no corner, periodically dump pair angles so we
// can see why detection is failing from chrome://inspect.
function maybeLogDiagnostic(walls, floors) {
  if (walls.length < 2) return
  const now = Date.now()
  if (now - _lastDiagnosticAt < DIAGNOSTIC_INTERVAL_MS) return
  _lastDiagnosticAt = now

  console.info(
    `[corner] diag: ${walls.length} remembered walls, ${floors.length} remembered floors, no corner found (memory window ${WALL_MEMORY_MS}ms)`
  )
  for (let i = 0; i < walls.length; i++) {
    for (let j = i + 1; j < walls.length; j++) {
      const dot = Math.abs(walls[i].normal.dot(walls[j].normal))
      const angle = (Math.acos(Math.min(1, dot)) * 180) / Math.PI
      console.info(`[corner]   wall ${i}↔${j}: angle between normals = ${angle.toFixed(0)}°`)
    }
  }
}

function poseToWorld(plane, frame, localSpace) {
  if (!plane.polygon || plane.polygon.length === 0) return null
  const pose = frame.getPose(plane.planeSpace, localSpace)
  if (!pose) return null

  const m = new THREE.Matrix4().fromArray(pose.transform.matrix)

  // Surface normal is the +Y axis of planeSpace, in localSpace coords.
  // Use the orientation quaternion directly (matches what planes.js does)
  // — fewer assumptions than reading matrix indices.
  const q = pose.transform.orientation
  const quat = new THREE.Quaternion(q.x, q.y, q.z, q.w)
  const normal = new THREE.Vector3(0, 1, 0).applyQuaternion(quat).normalize()

  const worldPoints = new Array(plane.polygon.length)
  let cx = 0
  let cy = 0
  let cz = 0
  for (let i = 0; i < plane.polygon.length; i++) {
    const p = plane.polygon[i]
    const v = new THREE.Vector3(p.x, p.y, p.z).applyMatrix4(m)
    worldPoints[i] = v
    cx += v.x
    cy += v.y
    cz += v.z
  }
  const n = plane.polygon.length
  const centroid = new THREE.Vector3(cx / n, cy / n, cz / n)
  const averageY = cy / n

  return { worldPoints, normal, centroid, averageY }
}

function showMarker(position) {
  if (!_markerMesh) {
    const geometry = new THREE.CylinderGeometry(
      MARKER_RADIUS_M,
      MARKER_RADIUS_M,
      MARKER_HEIGHT_M,
      20
    )
    const material = new THREE.MeshBasicMaterial({
      color: MARKER_COLOR,
      transparent: true,
      opacity: 0.85,
      depthTest: false
    })
    _markerMesh = new THREE.Mesh(geometry, material)
    _markerMesh.renderOrder = 600
    _markerMesh.frustumCulled = false
    _cornerGroup.add(_markerMesh)
  }
  // Position is the floor-level point; offset up by half-height so the
  // pole sits on the floor.
  _markerMesh.position.set(position.x, position.y + MARKER_HEIGHT_M / 2, position.z)
  _markerMesh.visible = true
}

function hideMarker() {
  if (_markerMesh) _markerMesh.visible = false
}
